import { Box, Container, Alert, Center } from "@mantine/core";
import { useParams } from "react-router-dom";
import { DynamicForm, useFormFields, useGoogleFont } from "@/features/application-form";
import { SiteHeaderFrame } from "@/features/site-header";
import { readHeaderUrlOverride, resolveHeaderUrl } from "@/config/headerOrigins";
import { CommonFooter, LoadingSpinner } from "@nw4/ui";
import { tokens } from "@nw4/theme";

export const App = () => {
    const { formGuid } = useParams();
    const { config, siteConfig, layout, loading, error } = useFormFields(formGuid);

    useGoogleFont(layout.typography.fontFamily);

    // The chosen font falls back to the theme stack if it fails to load.
    const fontFamily = layout.typography.fontFamily
        ? `"${layout.typography.fontFamily}", ${tokens.typography.fontFamily}`
        : undefined;

    // The plain font-family is needed on top of the variables: `body` already computed
    // the theme font and the elements below inherit that value, not the variable.
    // Headings read their own variable, so Title would otherwise keep the theme font.
    // Monospace is left alone: it is used on purpose, e.g. by the social URL prefix.
    const fontStyle = fontFamily
        ? {
              fontFamily,
              "--mantine-font-family": fontFamily,
              "--mantine-font-family-headings": fontFamily,
          }
        : undefined;

    // Only an allowed origin may be framed above the form. The override is a
    // development aid and is validated the same way.
    const headerUrl = resolveHeaderUrl(readHeaderUrlOverride() ?? layout.header.url);

    if (loading) {
        return <LoadingSpinner message="Caricamento configurazione form..." />;
    }

    if (error) {
        return (
            <Center h="100vh">
                <Alert color="red" title="Errore">
                    {error}
                </Alert>
            </Center>
        );
    }

    if (!config) {
        return (
            <Center h="100vh">
                <Alert color="yellow" title="Attenzione">
                    Nessuna configurazione form disponibile
                </Alert>
            </Center>
        );
    }

    return (
        <Box
            bg={layout.colors.pageBackground}
            mih="100vh"
            // Flex column so the header height is absorbed by the 100vh instead of
            // adding to it, which would leave a permanent scrollbar.
            style={{ ...fontStyle, display: "flex", flexDirection: "column" }}
        >
            {headerUrl && (
                <SiteHeaderFrame
                    url={headerUrl}
                    initialHeight={layout.header.initialHeight}
                    timeout={layout.header.timeout}
                />
            )}
            <Container size={layout.form.maxWidth} py="xl" style={{ flex: 1, width: "100%" }}>
                <DynamicForm
                    config={config}
                    siteConfig={siteConfig}
                    layout={layout}
                    hasSiteHeader={Boolean(headerUrl)}
                    formGuid={formGuid}
                />
            </Container>
            <CommonFooter />
        </Box>
    );
};
